/** TREPTELE dintre două cote ale unui etaj (`Stair`, format 3.12), partea
 *  pură: cadrul rampei din amprenta ei și sensul urcării, liniile treptelor
 *  pentru planșă, săgeata «urcă». Macheta (`trepte3d.ts`) ridică blocurile pe
 *  același cadru, ca planul și macheta să pună aceeași treaptă în același loc. */

export interface CadruTrepte {
  /** Versorul pe lungul rampei, în sensul urcării, și cel pe lățime. */
  u: [number, number];
  v: [number, number];
  /** Întinderea amprentei pe `u` (de jos în sus) și pe `v`. */
  s0: number; s1: number; t0: number; t1: number;
}

/** Cadrul rampei: proiecția amprentei pe sensul urcării. `null` pe o amprentă
 *  degenerată. */
export function cadruTrepte(poly: readonly number[][], deg: number): CadruTrepte | null {
  if (poly.length < 3) return null;
  const a = (deg * Math.PI) / 180;
  const u: [number, number] = [Math.cos(a), Math.sin(a)];
  const v: [number, number] = [-Math.sin(a), Math.cos(a)];
  let s0 = Infinity, s1 = -Infinity, t0 = Infinity, t1 = -Infinity;
  for (const [x, y] of poly) {
    const s = x * u[0] + y * u[1], t = x * v[0] + y * v[1];
    s0 = Math.min(s0, s); s1 = Math.max(s1, s); t0 = Math.min(t0, t); t1 = Math.max(t1, t);
  }
  if (!(s1 - s0 > 0.05) || !(t1 - t0 > 0.05)) return null;
  return { u, v, s0, s1, t0, t1 };
}

/** Punctul din cadru înapoi în plan. */
export const dinCadru = (c: CadruTrepte, s: number, t: number): [number, number] =>
  [s * c.u[0] + t * c.v[0], s * c.u[1] + t * c.v[1]];

/** Liniile contratreptelor pe planșă: `n` contratrepte înseamnă `n − 1` trepte
 *  pe amprentă (ultima contratreaptă dă în placa de sus), deci `n − 1` linii
 *  peste prima — fiecare pe toată lățimea rampei. */
export function liniileTreptelor(poly: readonly number[][], deg: number, n: number): [number, number, number, number][] {
  const c = cadruTrepte(poly, deg);
  if (!c || n < 2) return [];
  const pas = (c.s1 - c.s0) / (n - 1);
  const out: [number, number, number, number][] = [];
  for (let i = 0; i <= n - 1; i++) {
    const s = c.s0 + i * pas;
    const [x0, y0] = dinCadru(c, s, c.t0), [x1, y1] = dinCadru(c, s, c.t1);
    out.push([x0, y0, x1, y1]);
  }
  return out;
}

/** Săgeata «urcă» a planșei: de la baza rampei la capătul de sus, pe mijlocul
 *  lățimii — coada, vârful și cele două aripi ale vârfului. */
export function sageataUrca(poly: readonly number[][], deg: number):
    { coada: [number, number]; varf: [number, number]; aripi: [[number, number], [number, number]] } | null {
  const c = cadruTrepte(poly, deg);
  if (!c) return null;
  const t = (c.t0 + c.t1) / 2;
  const coada = dinCadru(c, c.s0 + 0.15, t), varf = dinCadru(c, c.s1 - 0.1, t);
  const l = Math.min(0.25, (c.t1 - c.t0) / 4);
  return { coada, varf,
           aripi: [dinCadru(c, c.s1 - 0.1 - l, t - l * 0.6), dinCadru(c, c.s1 - 0.1 - l, t + l * 0.6)] };
}

/** Cadrul unui dreptunghi rotit oarecare (platforma): centrul, laturile și
 *  unghiul primei muchii. */
export function cadruDreptunghi(poly: readonly number[][]): { cx: number; cy: number; w: number; l: number; deg: number } | null {
  if (poly.length < 3) return null;
  const [a, b] = [poly[0], poly[1]];
  const deg = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
  const c = cadruTrepte(poly, deg);
  if (!c) return null;
  const [cx, cy] = dinCadru(c, (c.s0 + c.s1) / 2, (c.t0 + c.t1) / 2);
  return { cx, cy, w: c.s1 - c.s0, l: c.t1 - c.t0, deg };
}

/** Cota scrisă pe placă: «−0,90» / «+0,30». */
export const cotaScrisa = (cota: number): string =>
  `${cota < 0 ? "−" : "+"}${Math.abs(cota).toFixed(2).replace(".", ",")}`;
